import React, {
  CSSProperties,
  forwardRef,
  MouseEvent,
  MouseEventHandler,
  ReactNode,
  TouchEventHandler,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import cn from "classnames";
import deepmerge from "deepmerge";
import isEqual from "lodash-es/isEqual";

import type User from "@mapmycustomers/shared/types/User";

import CardType from "@app/enum/dashboard/CardType";
import dragStyles from "@app/scene/dashboard/components/styles/common.module.scss";
import getCardByType from "@app/scene/dashboard/utils/getCardByType";
import CardConfiguration from "@app/types/dashboard/CardConfiguration";
import BaseCardViewState from "@app/types/dashboard/cards/BaseCardViewState";
import DashboardCard from "@app/types/dashboard/DashboardCard";
import Scope from "@app/types/dashboard/Scope";

import styles from "./GridCard.module.scss";

const mergeConfigs = (
  a: CardConfiguration<any, any, any>,
  b: Partial<CardConfiguration<any, any, any>>
) =>
  deepmerge(a, b, {
    // override array fields instead of merging arrays
    arrayMerge: (destinationArray, sourceArray) => sourceArray,
  });

interface GridCardProps {
  children?: ReactNode; // required by Grid https://github.com/react-grid-layout/react-grid-layout?tab=readme-ov-file#custom-child-components-and-draggable-handles
  className?: string;
  configuration: CardConfiguration<any, any, any>;
  currentUserId: User["id"];
  draggable?: boolean;
  id: DashboardCard["id"];
  onCopy?: (cardId: DashboardCard["id"]) => void;
  onEdit?: (cardId: DashboardCard["id"]) => void;
  onMouseDown?: MouseEventHandler; // required by Grid https://github.com/react-grid-layout/react-grid-layout?tab=readme-ov-file#custom-child-components-and-draggable-handles
  onMouseUp?: MouseEventHandler; // required by Grid https://github.com/react-grid-layout/react-grid-layout?tab=readme-ov-file#custom-child-components-and-draggable-handles
  onRemove?: (cardId: DashboardCard["id"]) => void;
  onSaveCard?: (payload: {
    cardId: DashboardCard["id"];
    configuration: CardConfiguration<any, any, any>;
    fullWidth?: DashboardCard["fullWidth"];
    scope: Scope;
    updateGoalOverrides: boolean;
    updateMetaData?: Pick<DashboardCard, "updatedAt" | "updatedBy">;
    updatePreferencesOverrides: boolean;
  }) => void;
  onTouchEnd?: TouchEventHandler; // required by Grid https://github.com/react-grid-layout/react-grid-layout?tab=readme-ov-file#custom-child-components-and-draggable-handles
  scope: Scope;
  style?: CSSProperties; // required by Grid https://github.com/react-grid-layout/react-grid-layout?tab=readme-ov-file#custom-child-components-and-draggable-handles
  type: CardType;
  viewState: any;
}

const GridCard = forwardRef<HTMLDivElement, GridCardProps>(
  (
    {
      id,
      children,
      className,
      configuration,
      currentUserId,
      draggable,
      onCopy,
      onEdit,
      onMouseDown,
      onMouseUp,
      onRemove,
      onSaveCard,
      onTouchEnd,
      scope,
      style,
      type,
      viewState,
    },
    ref
  ) => {
    const CardContentComponent = useMemo(() => getCardByType(type)?.CardContentComponent, [type]);

    const [temporaryConfiguration, setTemporaryConfiguration] = useState(configuration);
    useEffect(() => setTemporaryConfiguration(configuration), [configuration]);
    const isTemporaryConfigurationDifferent = !isEqual(configuration, temporaryConfiguration);

    const handleChangeConfiguration = useCallback(
      (newConfiguration: Partial<CardConfiguration<any, any, any>>, temporarily: boolean) => {
        if (temporarily) {
          setTemporaryConfiguration((prev) => mergeConfigs(prev, newConfiguration));
        } else {
          onSaveCard?.({
            cardId: id,
            configuration: mergeConfigs(configuration, newConfiguration),
            scope,
            updateGoalOverrides: false,
            updateMetaData: {
              updatedAt: new Date().toISOString(),
              updatedBy: { id: currentUserId },
            },
            updatePreferencesOverrides: false, // passing false as this param is irrelevant to the changes we're going to save here
          });
        }
      },
      [configuration, currentUserId, id, onSaveCard, scope]
    );

    const handleResetTemporaryConfiguration = useCallback(
      () => setTemporaryConfiguration(configuration),
      [configuration]
    );

    const handleChangeView = useCallback(
      (viewState: BaseCardViewState) => {
        if (viewState.fullWidth !== undefined) {
          onSaveCard?.({
            cardId: id,
            configuration,
            fullWidth: viewState.fullWidth,
            scope,
            updateGoalOverrides: false,
            updatePreferencesOverrides: false, // passing false as this param is irrelevant to the changes we're going to save here
          });
        }
      },
      [configuration, id, onSaveCard, scope]
    );

    const handleCopy = useCallback(() => onCopy?.(id), [id, onCopy]);
    const handleEditCard = useCallback(() => onEdit?.(id), [id, onEdit]);
    const handleRemoveCard = useCallback(() => onRemove?.(id), [id, onRemove]);

    const [canDrag, setCanDrag] = useState(false);
    const handleMouseMove = useCallback((e: MouseEvent<HTMLDivElement>) => {
      const hasNonDraggableParent = !!(e.target as HTMLDivElement).closest(
        `.${dragStyles.nonDraggable}`
      );
      const hasDraggableParent = !!(e.target as HTMLDivElement).closest(`.${dragStyles.draggable}`);
      setCanDrag(hasDraggableParent && !hasNonDraggableParent);
    }, []);
    const handleMouseLeave = useCallback(() => setCanDrag(false), []);

    return (
      <div
        className={cn(styles.container, className, { [styles.draggable]: draggable && canDrag })}
        onMouseDown={onMouseDown}
        onMouseLeave={handleMouseLeave}
        onMouseMove={handleMouseMove}
        onMouseUp={onMouseUp}
        onTouchEnd={onTouchEnd}
        ref={ref}
        style={style}
      >
        <CardContentComponent
          configuration={configuration}
          draggable={draggable}
          id={id}
          onChangeConfiguration={handleChangeConfiguration}
          onChangeView={handleChangeView}
          onCopy={handleCopy}
          onEdit={handleEditCard}
          onRemove={handleRemoveCard}
          onResetTemporaryConfiguration={handleResetTemporaryConfiguration}
          scope={scope}
          temporaryConfiguration={
            isTemporaryConfigurationDifferent ? temporaryConfiguration : undefined
          }
          {...viewState}
        />
        {children}
      </div>
    );
  }
);

// don't forget to forward ref if using connect
export default GridCard;
